/**
 * Finite-number readers, TWO members that differ ONLY in whether they coerce: {@link asNumberOrNull} converts through
 * `Number(raw)`, {@link finiteNumberOrNull} refuses anything that is not already a number. Pick by whether a numeric
 * STRING is a valid input at your call site -- both answer `null` for a miss, so an adopted-wrong reader reads GREEN
 * and silently changes what the site accepts.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): finite-number coercion homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`), the
 * sibling of `trimToNull` in `const/string-guards.ts`. That ban is what previously mandated a private copy in every
 * utils/ consumer; `QUAL-NARROWING-HELPER-CLONE-001` is the paired forbid-rule on re-rolling this shape.
 *
 * COERCER, not a guard: absent (`null`/`undefined`), blank-string and non-finite inputs all become `null`, so a caller
 * cannot tell "absent" from "NaN" -- the shape a D1 write or JSON payload wants.
 *
 * BLANK IS NOT ZERO, and the WHITESPACE-only case is the one that got away: the guard tested `raw === ''` only, while
 * `Number('   ')` is 0 and `Number.isFinite(0)` is true, so a padded-empty field coerced to a real 0. Same defect
 * class as the GET-dispatch coercer (`utils/flow-engine/coerce-get-input.ts`), fixed there in the same change.
 * Deliberately NOT merged with `parseNumberLike` (`const/parse-number-like.ts`): this one coerces ANY type
 * through `Number(raw)` (so `true` -> 1), that one returns `null` for every non-number/non-string input.
 */
export function asNumberOrNull(raw: unknown): number | null {
	if (raw == null) return null;
	if (typeof raw === 'string' && raw.trim().length === 0) return null;
	return finiteNumberOrNull(Number(raw));
}

/**
 * The STRICT sibling of {@link asNumberOrNull}, and the two are NOT interchangeable in either direction: this one
 * COERCES NOTHING. Only a value that is already `typeof 'number'` and finite passes; a numeric STRING, a boolean and
 * every other type read `null` rather than being converted.
 *
 * That distinction is the whole reason both exist. `asNumberOrNull` is the shape a JSON payload or a form field wants,
 * where `'5'` genuinely means 5. This one is the shape a D1 NUMERIC COLUMN read wants, where a string arriving in a
 * numeric column means something upstream wrote the wrong type -- worth surfacing as an absent value rather than
 * repairing in the reader. Adopting the coercer at one of these call sites silently starts accepting the very inputs
 * the site exists to refuse, and nothing reds.
 *
 * `null` rather than a zero-fill is equally deliberate: a caller cannot tell "absent" from "zero" once a miss becomes
 * 0, which on a fee or a rows-changed reading is a fabricated measurement. The zero-filling shape is a separate
 * decision a caller spells at its own site.
 *
 * Homed here beside its coercing sibling so pure `utils/` leaves can value-import it without a forbidden cross-utils
 * sibling dependency (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`). Minted 2026-08-20: THREE leaves across `const/`,
 * `utils/` and `flows/` carried this body byte-identically under three different local names, each with its own
 * paragraph re-arguing the same no-coercion point, and `QUAL-CROSS-FILE-PRIVATE-FN-RENAMED-DUP-001` is what grouped
 * them. One decision, one spelling; `QUAL-NARROWING-HELPER-CLONE-001` carries the paired forbid-arm on re-rolling it.
 *
 * SPELLED AS THE GUARD CALL PLUS THE FALLBACK, and the direction of that composition is load-bearing rather than
 * cosmetic (inverted 2026-09-05; this reader spelled the `typeof` conjunction inline and {@link isFiniteNumber} was
 * `finiteNumberOrNull(value) !== null`). The two spellings compute the same function, so the swap changes no answer --
 * what it changes is who can SEE the pair. `QUAL-GUARD-COERCER-HALF-ADOPTION-001` derives its canonical pairings by
 * parsing coercer bodies shaped `return <guard>(value) ? value : <fallback>` out of the leaves it names, so while this
 * body spelled the test inline the derivation found NO pair for the finite family and every hand-rolled
 * `isFiniteNumber(x) ? x : null` beside an adopted guard was unreachable to it -- a gap that rule's own `statusReason`
 * recorded as owed to whoever next opened this leaf. Re-spelling the body is the cure AT THE SSOT rather than at the
 * matcher, exactly as `nonBlankStringOrEmpty` (`const/string-guards.ts`) took it. Do not invert it back.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the no-coercion narrowing this leaf owns, pinned below the utils
 * floor for the reason the paragraph above states -- pure `utils/` leaves bind it and may not value-import a sibling.
 */
export function finiteNumberOrNull(value: unknown): number | null {
	return isFiniteNumber(value) ? value : null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the KEYED read over {@link finiteNumberOrNull} -- one member of a
 * parsed record (a journal line, a JSON row) through the strict reader. The census leaves that read journal lines each
 * spelled this one-liner privately under their own names (`numberMember`, `finiteMember`); the caller owns WHICH member
 * is read, this leaf owns what a finite number is.
 */
export function finiteNumberMember(record: Readonly<Record<string, unknown>>, key: string): number | null {
	return finiteNumberOrNull(record[key]);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the WHOLE-RECORD read over {@link isFiniteNumber} -- every member
 * that is a finite number, under its own key, and every other member dropped rather than trusted. The parsed signal
 * rollup a call row carries (read by the flow and by the desk) and a keyed freshness-stamp map each looped it out by hand.
 */
export function finiteNumberMembers(record: Readonly<Record<string, unknown>>): Record<string, number> {
	const kept: Record<string, number> = {};
	for (const [key, value] of Object.entries(record)) {
		if (isFiniteNumber(value)) kept[key] = value;
	}
	return kept;
}

/**
 * The GUARD spelling of {@link finiteNumberOrNull} -- `true` exactly when that reader would answer a value, and the
 * finite family's BASE: this predicate carries the `typeof`-plus-finiteness test and the coercer beside it is spelled
 * as a call to it, so the two cannot drift on what a finite number IS.
 *
 * THE COMPOSITION RUNS THIS WAY ROUND SINCE 2026-09-05 and the reason is recorded on {@link finiteNumberOrNull}: the
 * reverse spelling (`finiteNumberOrNull(value) !== null` here, the inline conjunction there) computes the same
 * function while hiding the pair from `QUAL-GUARD-COERCER-HALF-ADOPTION-001`, whose derivation reads coercer bodies
 * shaped `return <guard>(value) ? value : <fallback>`. It differs from {@link isPositiveFinite} and
 * {@link isNullableNumber} in that respect on purpose -- `positiveFiniteOrNull` is a TWO-statement body no such
 * derivation can reach in either direction, so its guard stays the composed half.
 *
 * IT EXISTS BECAUSE THE READER CANNOT NARROW AT A GUARD SITE, which is a type fact rather than a preference. The
 * carriers this member serves are `if (typeof x === 'number' && Number.isFinite(x))` conditions whose BODY then uses
 * `x` as a `number`; rewriting them as `finiteNumberOrNull(x) !== null` adopts the SSOT and loses the narrowing, so
 * every body would need a cast or a rebind -- an adoption that makes the call site worse is an adoption nobody makes,
 * which is exactly why this shape stayed hand-rolled while the ternary shape was folded in 2026-08-20. A `value is
 * number` predicate keeps the narrowing and the SSOT at once.
 *
 * REJECTS BOTH INFINITIES AND `NaN`, and coerces nothing -- a numeric STRING answers `false`. Where a numeric string
 * is a valid input the site wants {@link asNumberOrNull} or {@link numericFieldOrNull} and its own test; the members
 * are not interchangeable and swapping one in reads GREEN while changing what the site accepts.
 *
 * Minted 2026-09-04 out of a measured class: a repo-wide backreferenced census over 32809 tracked `.ts`/`.tsx` files
 * found the positive conjunct (`typeof x === 'number' && Number.isFinite(x)`, not continued by a `> 0` / `>= 0` bound
 * and not a ternary) at 25 carriers and its DE MORGAN inverse (`typeof x !== 'number' || !Number.isFinite(x)`) at 17,
 * across `const/`, `utils/`, `tools/`, `flows/`, `components/`, `spa/` and `mfe/`. Neither shape is reachable by any
 * arm of `QUAL-NARROWING-HELPER-CLONE-001`: every finite arm there keys on the ternary-with-fallback body, which a
 * guard condition never reaches. The cure is minted here first; the paired arm is owed once the class is drained,
 * since a day-one throwing arm over a live queue is what this repo does not do.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the finite-number narrowing this leaf owns, pinned below the utils
 * floor for the same reason as its siblings -- pure `utils/` leaves bind it and may not value-import a sibling
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`).
 */
export function isFiniteNumber(value: unknown): value is number {
	return typeof value === 'number' && Number.isFinite(value);
}

/** {@link isFiniteNumber}, or a declared `null`: a member written as a finite number or as a recorded absence, the read
 *  the daemon row table and the countermeasure census cursor each spelled by hand. NOT {@link isNullableNumber}, which
 *  admits NaN and the infinities because it asks what TYPE arrived.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the finite narrowing's nullable arm, on the same floor. */
export function isNullableFiniteNumber(value: unknown): value is number | null {
	return value === null || isFiniteNumber(value);
}

/** {@link isFiniteNumber}, or absent: an optional member present only as a finite number.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the finite narrowing's optional arm, on the same floor. */
export function isOptionalFiniteNumber(value: unknown): value is number | undefined {
	return value === undefined || isFiniteNumber(value);
}

/** A whole number of either sign, narrowed -- the integer-minor-units check the ledger's leg validators refuse on.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the integer narrowing, beside the finite one for the same floor. */
export function isIntegerNumber(value: unknown): value is number {
	return typeof value === 'number' && Number.isInteger(value);
}

/** A whole number ABOVE ZERO, narrowed -- the whole-cent amount a refund, a dispute mirror and the wallet's refundable
 *  read each admit, which four money sites spelled as `!isIntegerNumber(x) || x <= 0` (2026-09-28 fold).
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-file {@link isIntegerNumber}. */
export function isPositiveInteger(value: unknown): value is number {
	return isIntegerNumber(value) && value > 0;
}

/**
 * {@link finiteNumberOrNull} with the POSITIVITY gate folded in -- the shape a KNOB reader wants, where 0 and every
 * negative are as unusable as `NaN` and must fall through to the declared default rather than be honoured.
 *
 * ZERO IS THE WHOLE POINT OF THE SEPARATE MEMBER, and it is a behaviour difference rather than a stricter mood: a
 * concurrency, page-budget or ceiling knob taking 0 binds a limit that admits NOTHING, so a caller that adopts
 * {@link finiteNumberOrNull} here silently starts accepting the one value that disables the surface it configures.
 * The witnessed shape is a ceiling: `limit > 0` is what keeps an unbound read from reading as "already at its
 * ceiling", and a 0 there would convict every census as a sample.
 *
 * COERCES NOTHING, exactly like the sibling it composes on -- a numeric STRING answers `null`. Where a numeric string
 * is a valid input take {@link asNumberOrNull} or {@link numericFieldOrNull} and spell the positivity test at the
 * callsite; the members are not interchangeable and swapping one in reads GREEN while changing what the site accepts.
 *
 * Minted 2026-08-23: FIVE leaves across `const/`, `utils/` and `tools/` spelled this three-conjunct predicate inline
 * with no canonical member to name -- a private `boundCeiling` in the DQ ceiling leaf plus four guard conditions --
 * and no arm of `QUAL-NARROWING-HELPER-CLONE-001` could see them, because its finite-number arm keys on the
 * ternary-with-null body that this predicate CONTINUES past. Composed on {@link finiteNumberOrNull} rather than
 * re-spelling the finite test, so the two cannot drift on what "finite" means. One decision, one spelling;
 * `QUAL-NARROWING-HELPER-CLONE-001` carries the paired forbid-arm on re-rolling it.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the positive-finite knob narrowing this leaf owns, pinned below
 * the utils floor for the same reason as its sibling -- pure `utils/` leaves bind it and may not value-import a
 * sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`).
 */
export function positiveFiniteOrNull(value: unknown): number | null {
	const finite = finiteNumberOrNull(value);
	return finite !== null && finite > 0 ? finite : null;
}

/**
 * The GUARD spelling of {@link positiveFiniteOrNull} -- `true` exactly when that reader would answer a value.
 * Spelled AS the reader plus a null test, so the predicate and the coercer cannot drift on what a positive finite
 * number IS.
 *
 * THIS PREDICATE REJECTS `Infinity`, deliberately, and the FINITENESS conjunct must not be dropped. A body spelled
 * `typeof value === 'number' && !Number.isNaN(value) && value > 0` admits `Infinity` and carries a dead NaN conjunct
 * besides (`NaN > 0` is already `false`) -- the exact defect this leaf's `positiveIntKnob` docstring records for the
 * deleted `coerceBound`. Its production gates are a lock TTL and a maxWait (`tools/fs/lock-manager.ts`), where an
 * `Infinity` TTL is a lock that can never be judged stale, so admitting it is not the contract anyone wants. No suite
 * pins the `Infinity` answer in either direction, so the guard's own docstring is the record of that decision.
 *
 * HOMED ON THE `const/` FLOOR, and it must stay there: a `utils/` home cannot serve `utils/` callers.
 * `const/lock-staleness-core.ts` imports it directly; `utils/agents/result-proc-wait-loop.ts` reaches it through a DI
 * seam member, which also carries a genuinely injected collaborator and a live test double, though from this floor the
 * atom behind it is importable directly -- a `utils/` home would forbid that (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`
 * bans the sibling import).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-file {@link positiveFiniteOrNull}.
 */
export function isPositiveFinite(value: unknown): value is number {
	return positiveFiniteOrNull(value) !== null;
}

/**
 * A positive INTEGER, else null -- the admission for a process id read back off a stamp or a flag, where a writer
 * that could not name its process wrote nothing, and a reader must treat anything else as "unknown", never as a pid.
 * `Number.isInteger` already refuses `NaN` and both infinities, so no separate finite test is spelled. Spelled inline
 * three times before this member: the daemon heartbeat's host pid and in-flight lock-holder pid
 * (`const/local-daemon-heartbeat-read.ts`) and the record-write hold's pid (`const/record-write-hold.ts`).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): pinned on the const floor, which every reader of a pid reaches.
 */
export function positiveIntegerOrNull(value: unknown): number | null {
	return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * {@link finiteNumberOrNull} with a NON-STRICT lower bound folded in -- the shape a MEASUREMENT reader wants, where a
 * genuine zero is the reading and only a negative one is a broken probe.
 *
 * ZERO IS THE WHOLE POINT OF THE SEPARATE MEMBER, exactly as it is for {@link positiveFiniteOrNull} and in the
 * OPPOSITE direction, which is why neither may be adopted at the other's call site. A knob taking 0 binds a limit that
 * admits nothing, so `positiveFiniteOrNull` must reject it; a host probe answering 0 is reporting an idle machine, an
 * empty volume or a zero count, so rejecting it here would discard exactly the measurements the reader exists to
 * record. Swapping the two reads GREEN and silently changes which answers survive.
 *
 * NEGATIVE IS REJECTED BECAUSE THE QUANTITIES ARE COUNTS AND BYTE TOTALS, for which a negative value is not a small
 * reading but a probe that failed in a way it did not report -- the same class as `NaN`, and collapsed to the same
 * ABSENT so a later reader cannot tell one broken probe from another and act on either.
 *
 * COERCES NOTHING, exactly like the sibling it composes on -- a numeric STRING answers `null`. Composed on
 * {@link finiteNumberOrNull} rather than re-spelling the finite test, so the members cannot drift on what "finite"
 * means.
 *
 * ONE BODY FOR THE COUNT READERS: the two host probes (`const/host-free-space.ts`, feeding the statfs block-arithmetic
 * fold, and `utils/hooks/post-tool-use-failure-host-state.ts`, feeding the failure envelope's host record) both narrow
 * through it, and a private copy of this non-strict bound is a `QUAL-NARROWING-HELPER-CLONE-001` row.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the non-negative-finite narrowing this leaf owns, pinned below the
 * utils floor for the same reason as its siblings -- pure `utils/` leaves bind it and may not value-import a sibling
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`).
 */
export function nonNegativeFiniteOrNull(value: unknown): number | null {
	const finite = finiteNumberOrNull(value);
	return finite !== null && finite >= 0 ? finite : null;
}

/**
 * The bounds a {@link positiveIntKnob} read is decided by. `fallback` is REQUIRED because the whole point of the
 * reader is that an unusable input takes a DECLARED default rather than a zero or a `null` the caller must re-handle;
 * `min` and `max` are optional because an UNBOUNDED end is a real, load-bearing choice at some knobs (the AI-Search
 * per-run upload budget is deliberately uncapped above -- the operator naming a large limit is naming the run they
 * want) and passing a sentinel like `Number.POSITIVE_INFINITY` to express that would hide the decision inside a magic
 * value instead of stating it by omission.
 */
interface PositiveIntKnobBounds {
	/** Value returned when the input is not a positive finite number. */
	readonly fallback: number;
	/** Inclusive lower clamp applied AFTER flooring; omitted means the floored value stands. */
	readonly min?: number;
	/** Inclusive upper clamp applied AFTER flooring; omitted means the knob is uncapped above. */
	readonly max?: number;
}

/**
 * The KNOB READER: {@link positiveFiniteOrNull} floored to an integer and clamped into the declared bounds, with the
 * declared `fallback` for every input that is not a positive finite number. This is the whole shape a caller-supplied
 * tuning knob wants, in one place.
 *
 * ORDER IS THE BEHAVIOUR, and it is why this is a shared body rather than a per-site idiom: the floor runs BEFORE the
 * clamps, so a fractional `0.5` under a `min: 1` reads as 1 rather than as 0, while the same `0.5` with NO `min`
 * floors to 0. Reversing floor and clamp changes both answers, and nothing at a callsite reds when a hand-roll gets
 * the order wrong -- it just returns a plausible number.
 *
 * A LOWER CLAMP IS NOT DECORATION. The witnessed case is the AI-Search failed-receipt candidacy cap: a resolved 0
 * there binds `failures < 0` and silently makes EVERY row a non-candidate -- the sweep goes quiet rather than wide,
 * and no error is raised. Sites that genuinely want the floored value verbatim omit `min` and say so.
 *
 * COERCES NOTHING, inheriting that from {@link positiveFiniteOrNull}: a numeric STRING takes the fallback. Where the
 * knob is read off a query string or a form field, coerce with {@link asNumberOrNull} at the boundary first rather
 * than loosening this reader -- a knob that silently accepts `'0'` is the accident this family exists to prevent.
 *
 * ONE BODY FOR THE WHOLE FAMILY, and the bounds are the only thing a caller varies. A hand-rolled
 * floor-then-clamp-or-default drifts silently and cheaply: dropping the finiteness conjunct from the positivity test
 * lets `Infinity` floor to `Infinity` and become a live batch bound, which reads as correct at every call site and is
 * why a private respelling is a defect rather than a style. The paired forbid-rule on re-rolling the shape is
 * `QUAL-KNOB-CLAMP-INLINE-001`.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): homed beside the narrowing it composes on, for the same layer
 * reason -- its consumers are pure `utils/` leaves that may not value-import a `utils/` sibling
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`).
 */
export function positiveIntKnob(value: unknown, bounds: PositiveIntKnobBounds): number {
	const requested = positiveFiniteOrNull(value);
	if (requested === null) return bounds.fallback;
	const floored = Math.floor(requested);
	const lowered = bounds.min === undefined ? floored : Math.max(bounds.min, floored);
	return bounds.max === undefined ? lowered : Math.min(lowered, bounds.max);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the WIRE-FIELD numeric coercion -- a finite number from a `number`
 * or a numeric `string`, `null` for every other type. Homed here for the same layer reason as {@link asNumberOrNull}:
 * its consumers are a const leaf, two pure `utils/` leaves and a `tools/` normalizer, and no `utils/` sibling is
 * importable from the first three.
 *
 * TYPE-GATED, NOT `Number()`-COERCED, and that is the whole discriminator against {@link asNumberOrNull}. Only the two
 * wire-representable types are read, so a boolean answers `null` here where `asNumberOrNull` answers 1, and a blank
 * string reads as the finite 0 (`Number('')`) where `asNumberOrNull` reports the gap. Both divergences are LOAD-BEARING
 * for the OTLP ingest contract this body serves -- OTLP/JSON serializes int64 fields as decimal strings, and the span
 * arms treat 0 as UNSPECIFIED/UNSET -- which is why folding that reader onto `asNumberOrNull` was adjudicated and
 * declined. Where a blank string must report a GAP rather than a zero, take {@link numericCellOrNull} instead.
 */
export function numericFieldOrNull(value: unknown): number | null {
	if (typeof value === 'number') return finiteNumberOrNull(value);
	if (typeof value !== 'string') return null;
	return finiteNumberOrNull(Number(value));
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the STORED-CELL numeric coercion -- {@link numericFieldOrNull} with
 * a blank string reported as a GAP rather than as `Number('')`'s finite 0. D1 hands INTEGER columns back as numbers
 * while a text-typed column arrives as a string, and an R2 SQL aggregate hands int64 back as a string on some shapes,
 * so both types are read; an empty cell is an absent reading and a non-numeric one is a gap, never a zero.
 *
 * Composed on its sibling rather than re-spelling the coercion, so the two cannot drift on what "finite" means.
 */
export function numericCellOrNull(value: unknown): number | null {
	if (typeof value === 'string' && value.length === 0) return null;
	return numericFieldOrNull(value);
}

/**
 * The bare TYPE gate with NO finiteness filter: a `number` passes as-is (NaN and the infinities included), everything
 * else is null. It lives in this generic leaf because its importers -- linting, rca, telemetry, messaging, the app flow
 * client -- share no domain. NOT folded onto {@link finiteNumberOrNull}: the two differ on exactly NaN/Infinity, and
 * each caller is measured against this semantic.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): The bare TYPE gate with NO finiteness filter: a `number` passes as-is (NaN and the infinities included), everything else is null.
 */
export function bareNumberOrNull(v: unknown): number | null {
	return typeof v === 'number' ? v : null;
}

/**
 * The OPTIONAL-FIELD twin of {@link bareNumberOrNull} -- identical in every input it accepts and rejects, differing
 * ONLY in the absent value: `undefined` rather than `null`. Exactly the relationship {@link trimToUndefined} has to
 * `trimToNull` in `const/string-guards.ts`, and it exists for the same reason: an interface whose property is
 * `field?: number` will not take a `null`, so the reader that answers `null` cannot be adopted there and a caller
 * writing `?? undefined` around it re-states the absent decision a second time.
 *
 * IT INHERITS THE NO-FINITENESS-FILTER CONTRACT and that is the part an adopter must read: `NaN` and both infinities
 * PASS, because the question is what TYPE arrived, not whether the value is usable arithmetic. Where the arithmetic
 * matters take {@link finiteNumberOrNull} (or {@link isFiniteNumber} at a guard site) instead -- swapping this member
 * in there reads GREEN while silently admitting a `NaN` the site exists to refuse. Where a numeric STRING is a valid
 * input take {@link asNumberOrNull} or {@link numericFieldOrNull}; this one coerces nothing.
 *
 * Minted 2026-09-05 out of a measured class: a backreferenced PCRE2 census over the tracked `.ts`/`.tsx` corpus found
 * `typeof x === 'number' ? x : undefined` at SIXTEEN carriers across `const/`, `utils/`, `tools/`, `flows/`,
 * `components/`, `apps/` and `microservices/` -- five of them whole helper bodies under five different private names
 * (the since-deleted `asRecordIndex` in `utils/catalog/catalog-store-shape.ts` and `asNum` in the whole-file
 * `apps/admin-federa/.../offers/offer-field-num.ts`, plus `responseNodeStatus`, `readIntentAmountRefunded` and
 * `numericMemberOfKind`) -- with no member to name. `bareNumberOrNull` beside it had covered the `null` half since
 * 2026-09-02, so the
 * half-adopted family is exactly the shape `QUAL-NARROWING-HELPER-CLONE-001`'s stringOrNull-family arm records for the
 * string trio: three members of one shape differing only in the absent value, where a local re-declaration is a fourth
 * member nobody reconciled against the others. Composed on the sibling rather than re-spelling the type gate, so the
 * two cannot drift on what "a number arrived" means; the paired forbid-arm is that rule's number-fallback arm.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-file {@link bareNumberOrNull}, pinned below the
 * utils floor for the same reason as its siblings -- pure `utils/` leaves bind it and may not value-import a sibling
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`).
 */
export function asNumberOrUndefined(value: unknown): number | undefined {
	const bare = bareNumberOrNull(value);
	return bare === null ? undefined : bare;
}

/**
 * The ZERO-DEFAULTED third member of the {@link bareNumberOrNull} family -- identical in every input it accepts and
 * rejects to that member and to {@link asNumberOrUndefined}, differing ONLY in the absent value: `0` rather than
 * `null` or `undefined`. It is the numeric counterpart of `stringOrEmpty` (`const/string-guards.ts`), which is the
 * string family's own zero-valued member, and it exists for the same reason: a caller whose next operation is
 * ARITHMETIC or a counter increment cannot take a `null`, so the reader that answers `null` is unadoptable there and
 * a caller writing `?? 0` around it re-states the absent decision a second time.
 *
 * IT COERCES NOTHING, and that is the part an adopter must read: a numeric STRING is NOT a number here and reads as
 * absent, so `numberOrZero('5')` is `0`. Where a numeric string is a valid input take {@link asNumberOrNull} or
 * {@link numericFieldOrNull} instead -- swapping this member in there reads GREEN while silently narrowing what the
 * site accepts. IT ALSO INHERITS THE NO-FINITENESS-FILTER CONTRACT from its base: `NaN` and both infinities PASS as
 * themselves rather than collapsing to `0`, because the question is what TYPE arrived, not whether the value is
 * usable arithmetic. Where the arithmetic matters take `finiteNumberOrNull(v) ?? 0`, which is a different total
 * function and is spelled differently on purpose.
 *
 * Minted 2026-09-05 out of a measured class the family's own paired forbid-rule had recorded as UNREACHABLE: a
 * backreferenced census over the tracked `.ts`/`.tsx` corpus found `typeof x === 'number' ? x : 0` at 93 carriers
 * across `flows/`, `tools/`, `utils/`, `microservices/`, `components/`, `const/` and `apps/`, every one of them
 * same-receiver. `QUAL-NARROWING-HELPER-CLONE-001`'s scalar-or-fallback arm names the exact reason it could not
 * convict them: "the string arm admits `''` because `stringOrEmpty` exists, while `const/number-guards.ts` exports no
 * zero-valued member". This member is that missing export, so the ground the arm recorded for its own narrowness is
 * retired and the arm's number branch widens to `0` in the same change. Composed on {@link bareNumberOrNull} rather
 * than re-spelling the type gate, so the three members cannot drift on what "a number arrived" means.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-file {@link bareNumberOrNull}, pinned below the
 * utils floor for the same reason as its siblings -- pure `utils/` leaves bind it and may not value-import a sibling
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`).
 */
export function numberOrZero(value: unknown): number {
	const bare = bareNumberOrNull(value);
	return bare === null ? 0 : bare;
}

/**
 * The COLUMN-SHAPE guard: `number | null` and nothing else -- what a nullable NUMERIC column must hold before a reader
 * dereferences it.
 *
 * IT IS NOT A FINITENESS TEST AND MUST NOT BE FOLDED ONTO {@link isFiniteNumber}, which is the confusion this member
 * exists to make impossible to make silently. `NaN` and both infinities PASS here, because the question is what TYPE
 * arrived in the column, not whether the value is usable arithmetic -- an explicit `null` is a recorded absence the
 * caller keeps, while `isFiniteNumber(null)` is `false` and would collapse a recorded null onto the same verdict as a
 * malformed string. Where the arithmetic matters, the caller takes {@link finiteNumberOrNull} AFTER this gate.
 *
 * BOTH CONJUNCT ORDERS ARE ONE DECISION and this is where it is spelled. Minted 2026-09-04 out of a byte-identical
 * twin: `utils/knowledgebase/entry-row-guard.ts` (the shared `questions_answers` list projection, where a malformed
 * nullable column DROPS the row from both panes) and `const/standard-measure-projection.ts` (the standards
 * envelope reader, where it THROWS a named malformed-envelope error) each declared it privately under this exact name.
 * Two surfaces disagreeing on what "a nullable numeric column" means is precisely the drift the first of those files'
 * own docstring says hoisting a guard is meant to prevent.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): homed on the const floor because one of its two consumers is a
 * pure `utils/` leaf that may not value-import a `utils/` sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`).
 */
export function isNullableNumber(value: unknown): value is number | null {
	return value === null || typeof value === 'number';
}

// NAN_FALLBACK

/** `value`, or `safeDefault` when it is NaN -- the fallback both number coercers (`coerceNumber` and
 *  `coerceFormattedNumber`, `const/coerce-number.ts`) apply to a number input and to a parse result. NaN ONLY: the
 *  infinities pass as themselves, which is the contract those coercers already carried.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the coercer leaf named above sits on this same floor (since
 *  2026-09-27, so that utils readers barred from a utils sibling can reach it) and may import nothing higher. */
export const nanToDefault = (value: number, safeDefault: number): number => (Number.isNaN(value) ? safeDefault : value);
